import { useMemo } from 'react';

import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IntlShape } from 'react-intl';

import {
  BookingEngineType,
  IIssueReportClient,
  IIssueReportContext,
  IIssueReportPayload,
  IssueReportStep,
  ISSUE_REPORT_ALLOWED_CONTENT_TYPES,
  ISSUE_REPORT_ALLOWED_EXTENSIONS,
  ISSUE_REPORT_MAX_IMAGES,
  ISSUE_REPORT_MAX_IMAGE_SIZE,
} from '@share/common-types';
import {
  FunctionalRootState,
  getLastApiError,
  toSessionGuid,
  CARS,
  CONDO,
  CONDOBOOK,
  BOOK,
  HOTEL,
  SEARCH,
  WEEKS,
  DEALS,
  DETAILS,
} from '@share/utils';

const ENGINE_SEGMENT = 1;
const SUB_PAGE_SEGMENT = 2;
const BYTES_IN_MB = 1024 * 1024;

// Paths are /{account}/{page}/..., so the engine lives in the second segment.
const getPathSegments = (pathname: string): string[] =>
  (pathname ?? '').split('/').filter((segment) => segment.length > 0);

export const getBookingEngine = (pathname: string): BookingEngineType => {
  const page = getPathSegments(pathname)[ENGINE_SEGMENT];

  if (page === CARS) {
    return BookingEngineType.Car;
  }

  if (page === CONDO || page === CONDOBOOK || page === WEEKS) {
    return BookingEngineType.Condo;
  }

  if (page === HOTEL || page === SEARCH || page === BOOK) {
    return BookingEngineType.Hotel;
  }

  // Home, dashboard, account and every other cross-engine page.
  return BookingEngineType.Both;
};

export const getStep = (pathname: string, engine: BookingEngineType): string => {
  const segments = getPathSegments(pathname);
  const page = segments[ENGINE_SEGMENT];
  const subPage = segments[SUB_PAGE_SEGMENT];

  if (engine === BookingEngineType.Car) {
    if (subPage === SEARCH) {
      return IssueReportStep.SearchResults;
    }
    if (subPage === DETAILS) {
      return IssueReportStep.VehicleDetail;
    }
    return IssueReportStep.Search;
  }

  if (engine === BookingEngineType.Condo) {
    if (page === CONDOBOOK) {
      return IssueReportStep.Checkout;
    }
    if (subPage === DEALS || subPage === SEARCH) {
      return IssueReportStep.SearchResults;
    }
    if (subPage) {
      return IssueReportStep.PropertyDetail;
    }
    return IssueReportStep.Search;
  }

  if (engine === BookingEngineType.Hotel) {
    if (page === BOOK) {
      return IssueReportStep.Checkout;
    }
    if (page === HOTEL) {
      return IssueReportStep.PropertyDetail;
    }
    return IssueReportStep.SearchResults;
  }

  return IssueReportStep.Other;
};

export const getClientInfo = (): IIssueReportClient => ({
  browser: navigator.userAgent,
  locale: navigator.language,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
});

// Only the few fields helpdesk needs to reproduce. Nothing from `card` or the
// guest forms is read here: the payload is stored and emailed in plaintext.
export const useIssueReportContext = (): IIssueReportContext => {
  const history = useHistory();

  const hotelsStore = useSelector((state: FunctionalRootState) => state.hotelsStore);
  const condosStore = useSelector((state: FunctionalRootState) => state.condosStore);
  const carsStore = useSelector((state: FunctionalRootState) => state.carsStore);
  const locationsStore = useSelector((state: FunctionalRootState) => state.locationsStore);
  const datesStore = useSelector((state: FunctionalRootState) => state.datesStore);
  const roomsStore = useSelector((state: FunctionalRootState) => state.roomsStore);
  const condosLocationPickerStore = useSelector(
    (state: FunctionalRootState) => state.condosLocationPickerStore,
  );
  const condoStrictDatesStore = useSelector(
    (state: FunctionalRootState) => state.condoStrictDatesStore,
  );
  const condoGuestsStore = useSelector((state: FunctionalRootState) => state.condoGuestsStore);
  const carsLocationsStore = useSelector((state: FunctionalRootState) => state.carsLocationsStore);
  const carsDatesStore = useSelector((state: FunctionalRootState) => state.carsDatesStore);
  const carsDriverStore = useSelector((state: FunctionalRootState) => state.carsDriverStore);
  const currencyStore = useSelector((state: FunctionalRootState) => state.currencyStore);
  const reviewBookStore = useSelector((state: FunctionalRootState) => state.reviewBookStore);
  const condoReviewBookStore = useSelector(
    (state: FunctionalRootState) => state.condoReviewBookStore,
  );
  const carsReviewBookStore = useSelector(
    (state: FunctionalRootState) => state.carsReviewBookStore,
  );

  const { pathname } = history.location;

  return useMemo(() => {
    const bookingEngine = getBookingEngine(pathname);
    const step = getStep(pathname, bookingEngine);
    const currency = get(currencyStore, 'selectedCurrency.code', undefined);

    let sessionKey: string | null | undefined;
    let request: any;
    let selection: any;
    let ui: any;

    if (bookingEngine === BookingEngineType.Car) {
      sessionKey = get(carsStore, 'sessionKey.value');
      request = {
        pickUpLocation: get(carsLocationsStore, 'pickUp.code'),
        pickUpType: get(carsLocationsStore, 'pickUp.type'),
        pickUpDate: carsDatesStore?.startDate,
        pickUpTime: carsDatesStore?.startDateTime,
        returnLocation: get(carsLocationsStore, 'dropOff.code'),
        returnDate: carsDatesStore?.endDate,
        returnTime: carsDatesStore?.endDateTime,
        countryISO: carsDriverStore?.driverCountry,
        driverAge: carsDriverStore?.driverAge,
        currency,
      };
      selection = {
        id: get(carsStore, 'selectedCar.id'),
        rentalCompany: get(carsStore, 'selectedCar.rentalCompany'),
        category: get(carsStore, 'selectedCar.category'),
        total: get(carsReviewBookStore, 'booking.bookingPrice.publicPrice'),
      };
      ui = { pageNumber: carsStore?.pageNumber, resultsCount: carsStore?.cars?.length };
    } else if (bookingEngine === BookingEngineType.Condo) {
      sessionKey = get(condosStore, 'sessionKey.value');
      request = {
        location: {
          id: get(condosLocationPickerStore, 'selectedLocation.id'),
          name: get(condosLocationPickerStore, 'selectedLocation.name'),
          locationType: get(condosLocationPickerStore, 'selectedLocation.locationType'),
        },
        checkIn: condoStrictDatesStore?.startDate,
        checkOut: condoStrictDatesStore?.endDate,
        adultsCount: condoGuestsStore?.adultsCount,
        childrenCount: condoGuestsStore?.kidsCount,
        bedroomsCount: condoGuestsStore?.bedroomsCount,
        currency,
      };
      selection = {
        condoId: get(condoReviewBookStore, 'condoBookingSummary.bookingCard.condoId'),
        unitName: get(condoReviewBookStore, 'condoBookingSummary.bookingCard.unitName'),
        checkIn: get(condoReviewBookStore, 'condoBookingSummary.bookingCard.checkIn'),
        checkOut: get(condoReviewBookStore, 'condoBookingSummary.bookingCard.checkOut'),
        bookingPrice: get(condoReviewBookStore, 'condoBookingSummary.bookingCard.balance'),
        currency: get(condoReviewBookStore, 'condoBookingSummary.bookingCard.currency', currency),
      };
      ui = {
        searchType: condoStrictDatesStore?.searchType,
        pageNumber: condosStore?.pageNumber,
        resultsCount: condosStore?.condos?.length,
      };
    } else if (bookingEngine === BookingEngineType.Hotel) {
      sessionKey = get(hotelsStore, 'sessionKey.value');
      request = {
        location: {
          name: get(locationsStore, 'selectedLocation.name'),
          code: get(locationsStore, 'selectedLocation.code'),
          type: get(locationsStore, 'selectedLocation.type'),
        },
        checkIn: datesStore?.startDate,
        checkOut: datesStore?.endDate,
        rooms: roomsStore?.roomsSelected?.map((room: any) => ({
          adultsCount: room?.adultsCount,
          kidsAges: room?.kidsAges,
        })),
        currency,
      };
      selection = {
        hotelId: get(hotelsStore, 'selectedHotel.id'),
        name: get(reviewBookStore, 'bookingSummary.bookingCard.name'),
        checkIn: get(reviewBookStore, 'bookingSummary.bookingCard.checkIn'),
        checkOut: get(reviewBookStore, 'bookingSummary.bookingCard.checkOut'),
        bookingPrice: get(reviewBookStore, 'bookingSummary.bookingCard.balance'),
        currency,
      };
      ui = {
        pageNumber: hotelsStore?.pageNumber,
        isMapView: hotelsStore?.isMapView,
        resultsCount: hotelsStore?.hotels?.length,
      };
    }

    const payload: IIssueReportPayload = {
      engine: bookingEngine,
      step,
      correlation: { sessionKey: sessionKey ?? null },
      request,
      selection,
      ui,
      lastError: getLastApiError(),
      client: getClientInfo(),
    };

    return {
      bookingEngine,
      step,
      sessionId: toSessionGuid(sessionKey),
      payload,
    };
  }, [
    pathname,
    hotelsStore,
    condosStore,
    carsStore,
    locationsStore,
    datesStore,
    roomsStore,
    condosLocationPickerStore,
    condoStrictDatesStore,
    condoGuestsStore,
    carsLocationsStore,
    carsDatesStore,
    carsDriverStore,
    currencyStore,
    reviewBookStore,
    condoReviewBookStore,
    carsReviewBookStore,
  ]);
};

// Set on <body> only while the trigger is actually rendered, so other floating
// controls can stack above it instead of leaving a gap when it is not there.
// The literal is duplicated in SCSS - keep both in sync.
export const REPORT_ISSUE_BODY_CLASS = 'has-report-issue';

// One attached image: either the auto-captured page screenshot or a file the
// member picked. `url` is an object URL for the thumbnail and must be revoked.
export interface IIssueReportImage {
  file: File;
  isAuto: boolean;
  url: string;
}

export const toIssueReportImage = (file: File, isAuto: boolean): IIssueReportImage => ({
  file,
  isAuto,
  url: URL.createObjectURL(file),
});

export const revokeImage = (image: IIssueReportImage): void => {
  URL.revokeObjectURL(image.url);
};

export interface IImageValidationResult {
  accepted: File[];
  error: string | null;
}

const getExtension = (name: string): string => {
  const index = name.lastIndexOf('.');
  return index < 0 ? '' : name.slice(index).toLowerCase();
};

// Mirrors the server-side rules so the member is told before the round trip.
// `current` already includes the auto-captured screenshot, so it counts against
// the same limit of 3 the API enforces.
export const validateImages = (
  current: File[],
  incoming: File[],
  intl: IntlShape,
): IImageValidationResult => {
  const accepted: File[] = [...current];
  let error: string | null = null;

  for (const file of incoming) {
    if (accepted.length >= ISSUE_REPORT_MAX_IMAGES) {
      error = intl.formatMessage(
        { id: 'report.issue.images.error.max' },
        { max: ISSUE_REPORT_MAX_IMAGES },
      );
      break;
    }

    if (
      !ISSUE_REPORT_ALLOWED_CONTENT_TYPES.includes(file.type) ||
      !ISSUE_REPORT_ALLOWED_EXTENSIONS.includes(getExtension(file.name))
    ) {
      error = intl.formatMessage({ id: 'report.issue.images.error.type' }, { name: file.name });
      continue;
    }

    if (file.size > ISSUE_REPORT_MAX_IMAGE_SIZE) {
      error = intl.formatMessage(
        { id: 'report.issue.images.error.size' },
        { name: file.name, max: ISSUE_REPORT_MAX_IMAGE_SIZE / BYTES_IN_MB },
      );
      continue;
    }

    if (file.size === 0) {
      error = intl.formatMessage({ id: 'report.issue.images.error.empty' }, { name: file.name });
      continue;
    }

    accepted.push(file);
  }

  return { accepted, error };
};
